import Comps from '@owenscorning/react-library';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  HouseImg,
  HouseSuccessImg,
  AffectedImg,
  AffectedSuccessImg,
  VentImg,
  VentSuccessImg,
  PhotoFrontImg,
  PhotoFrontImg2,
  PhotoBack,
  PhotoBack2,
  PhotoWideAngle,
  PhotoWideAngle2,
  PhotoWideAngle3,
  PhotoWideAngle4,
  PhotoCloseUp,
  PhotoCloseUp2,
  PhotoCloseUp3,
  PhotoCloseUp4,
  PhotoFastener,
  PhotoFastener2,
  PhotoFastener3,
  PhotoFastener4,
  PhotoVent,
  PhotoVent2,
  PhotoVent3,
  PhotoVent4,
  PhotoShingle,
  PhotoShingle2,
  ReleaseTapeImg,
} from '../../../../../../assets/images/warranty-claim';
import CTAButton from '../../../../CTAButton';
import Accordion from '../../../../ComponentLibrary/oc-accordion';
import InfoIcon from '../../../../OC/oc-info-icon';
import BackButton from '../../../shared/BackButton';
import ClaimArea from '../../helpers/claim-area';
import ReduxFileWidget from '../../helpers/redux-file-widget';
import { HeaderWrap, PhotoWrap, Subscript } from '../../helpers/styles';
import { isBeyond12Months } from '../../helpers/util';
import VideoThumbnails from '../../helpers/video-thumbnails';
import {
  incrementStep,
  setFormData,
  setAppData,
  setStep,
  savePhotos,
  decrementStep,
  notifyDocumentsUpload,
} from '../../redux/action';
import styles from '../07-documents/index.module.scss';

const { Modal } = Comps.Connect;

const WarrantyPhotos = () => {
  const dispatch = useDispatch();
  const mainState = useSelector(state => state);
  const {
    formData,
    metadata: { pqsResponse, warrantyDetails },
    isFetching,
    appData: { photosCount },
    disableSubmitBtn,
  } = mainState;

  const [modalOpen, setModal] = useState(false);
  const [modalType, setModalType] = useState('');

  const {
    groundLevelFrontHome,
    groundLevelBackHome,
    affectedAreaWideAngle,
    affectedAreaCloseUp,
    ventIntakePhoto,
    ventExhaustPhoto,
    shingleBack,
    shingleFront,
    releaseTape,
    fasteners,
  } = formData;

  const currentCount = [
    groundLevelFrontHome,
    groundLevelBackHome,
    affectedAreaWideAngle,
    affectedAreaCloseUp,
    ventIntakePhoto,
    ventExhaustPhoto,
    shingleFront,
    shingleBack,
    releaseTape,
    fasteners,
  ].reduce((sum, property) => sum + (property?.length ?? 0), 0);

  const saveText = photosCount === currentCount ? 'Next' : 'Save Updates';

  const validated =
    (!isFetching && groundLevelFrontHome) ||
    groundLevelBackHome ||
    affectedAreaWideAngle ||
    affectedAreaCloseUp ||
    ventIntakePhoto ||
    ventExhaustPhoto ||
    shingleBack ||
    shingleFront ||
    releaseTape ||
    fasteners;

  const goNext = () => {
    const installDate = warrantyDetails?.product_information?.date_of_installation;

    // Check if any documents/photos were uploaded in Steps 7 or 8
    const hasDocumentsOrPhotos = () => {
      const { proofOfRoofPurchase, proofOfOwnerShip } = formData;

      const fields = [
        proofOfRoofPurchase,
        proofOfOwnerShip,
        groundLevelFrontHome,
        groundLevelBackHome,
        affectedAreaWideAngle,
        affectedAreaCloseUp,
        ventIntakePhoto,
        ventExhaustPhoto,
      ];
      return fields.some(field => field !== null);
    };

    if (isBeyond12Months(installDate) && hasDocumentsOrPhotos()) {
      dispatch(notifyDocumentsUpload());
    } else {
      dispatch(incrementStep());
    }
  };

  return (
    <>
      <HeaderWrap>
        <BackButton
          analytics={{
            'data-track': 'form-back',
            'data-track-form-name': 'Submit Claim Photos',
            'data-track-form-step': 'step 8 - photos',
          }}
          onClick={() => dispatch(decrementStep())}
          backText="Documents"
        />
        <h2>Photos</h2>
        <ClaimArea pqsResponse={pqsResponse} />
        <p>
          A typical claim requires between 7–10 photographs. Please watch the short video below to understand the photos
          required then expand each section below to upload or take your images.{' '}
        </p>
      </HeaderWrap>
      <VideoThumbnails show="photo-requirements" mid />
      <Accordion
        label="Ground Level Photos"
        labelClassName={styles.labelHeader}
        imageSrc={groundLevelFrontHome && groundLevelBackHome ? HouseSuccessImg : HouseImg}
      >
        <h4>Front of Home</h4>
        <p>
          Ensure address is visible{' '}
          <InfoIcon
            clickHandler={() => {
              setModal(true);
              setModalType('frontOfHouse');
            }}
          />
        </p>
        <ReduxFileWidget
          onChange={files => dispatch(setFormData({ groundLevelFrontHome: files }))}
          fileUploadState={mainState.appData?.groundLevelFrontHome}
          saveFileUploadState={state => dispatch(setAppData({ groundLevelFrontHome: state }))}
        />
        <h4>Back of Home</h4>
        <p>
          Multiple images may be required{' '}
          <InfoIcon
            clickHandler={() => {
              setModal(true);
              setModalType('backOfHouse');
            }}
          />
        </p>
        <ReduxFileWidget
          onChange={files => dispatch(setFormData({ groundLevelBackHome: files }))}
          fileUploadState={mainState.appData?.groundLevelBackHome}
          saveFileUploadState={state => dispatch(setAppData({ groundLevelBackHome: state }))}
        />
      </Accordion>

      <Accordion
        label="Photos of Affected Area"
        labelClassName={styles.labelHeader}
        imageSrc={affectedAreaWideAngle && affectedAreaCloseUp ? AffectedSuccessImg : AffectedImg}
      >
        <h4>Wide Angle</h4>
        <p>
          Please ensure the issue is visible in the photos{' '}
          <InfoIcon
            clickHandler={() => {
              setModal(true);
              setModalType('wideAngle');
            }}
          />
        </p>
        <ReduxFileWidget
          onChange={files => dispatch(setFormData({ affectedAreaWideAngle: files }))}
          fileUploadState={mainState.appData?.affectedAreaWideAngle}
          saveFileUploadState={state => dispatch(setAppData({ affectedAreaWideAngle: state }))}
        />
        <h4>Close Up</h4>
        <p>
          Please ensure the issue is visible in the photos{' '}
          <InfoIcon
            clickHandler={() => {
              setModal(true);
              setModalType('closeUp');
            }}
          />
        </p>
        <ReduxFileWidget
          onChange={files => dispatch(setFormData({ affectedAreaCloseUp: files }))}
          fileUploadState={mainState.appData?.affectedAreaCloseUp}
          saveFileUploadState={state => dispatch(setAppData({ affectedAreaCloseUp: state }))}
        />
      </Accordion>

      <Accordion
        label="Ventilation Photos"
        labelClassName={styles.labelHeader}
        imageSrc={ventIntakePhoto && ventExhaustPhoto ? VentSuccessImg : VentImg}
      >
        <h4>Intake Photos</h4>
        <p>
          Multiple images may be required{' '}
          <InfoIcon
            clickHandler={() => {
              setModal(true);
              setModalType('intakeVent');
            }}
          />
        </p>
        <ReduxFileWidget
          onChange={files => dispatch(setFormData({ ventIntakePhoto: files }))}
          fileUploadState={mainState.appData?.ventIntakePhoto}
          saveFileUploadState={state => dispatch(setAppData({ ventIntakePhoto: state }))}
        />
        <h4>Exhaust Photos</h4>
        <p>
          Multiple images may be required{' '}
          <InfoIcon
            clickHandler={() => {
              setModal(true);
              setModalType('exhaustVent');
            }}
          />
        </p>
        <ReduxFileWidget
          onChange={files => dispatch(setFormData({ ventExhaustPhoto: files }))}
          fileUploadState={mainState.appData?.ventExhaustPhoto}
          saveFileUploadState={state => dispatch(setAppData({ ventExhaustPhoto: state }))}
        />
      </Accordion>

      <Accordion
        label="Shingle Photos"
        labelClassName={styles.labelHeader}
        imageSrc={shingleFront && shingleBack && releaseTape && fasteners ? AffectedSuccessImg : AffectedImg}
      >
        <h4>
          Front of Shingle{' '}
          <InfoIcon
            clickHandler={() => {
              setModal(true);
              setModalType('shingleFront');
            }}
          />
        </h4>
        <p>Click icon for photograph example</p>
        <ReduxFileWidget
          onChange={files => dispatch(setFormData({ shingleFront: files }))}
          fileUploadState={mainState.appData?.shingleFront}
          saveFileUploadState={state => dispatch(setAppData({ shingleFront: state }))}
        />
        <h4>
          Back of Shingle{' '}
          <InfoIcon
            clickHandler={() => {
              setModal(true);
              setModalType('shingleBack');
            }}
          />
        </h4>
        <p>Click icon for photograph example</p>
        <ReduxFileWidget
          onChange={files => dispatch(setFormData({ shingleBack: files }))}
          fileUploadState={mainState.appData?.shingleBack}
          saveFileUploadState={state => dispatch(setAppData({ shingleBack: state }))}
        />
        <h4>
          Release Tape{' '}
          <InfoIcon
            clickHandler={() => {
              setModal(true);
              setModalType('releaseTape');
            }}
          />
        </h4>
        <p>Click icon for photograph example</p>
        <ReduxFileWidget
          onChange={files => dispatch(setFormData({ releaseTape: files }))}
          fileUploadState={mainState.appData?.releaseTape}
          saveFileUploadState={state => dispatch(setAppData({ releaseTape: state }))}
        />
        <h4>
          Fasteners{' '}
          <InfoIcon
            clickHandler={() => {
              setModal(true);
              setModalType('fasteners');
            }}
          />
        </h4>
        <p>Click icon for photograph example</p>
        <ReduxFileWidget
          onChange={files => dispatch(setFormData({ fasteners: files }))}
          fileUploadState={mainState.appData?.fasteners}
          saveFileUploadState={state => dispatch(setAppData({ fasteners: state }))}
        />
      </Accordion>

      {saveText === 'Next' ? (
        <CTAButton
          onClick={goNext}
          data-track="form-save"
          data-track-form-name="Submit Claim Photos"
          data-track-form-step="step 8 - photos"
          element="button"
          text={isFetching ? 'Loading...' : saveText}
          type="submit"
          disabled={disableSubmitBtn}
        />
      ) : (
        <CTAButton
          onClick={() => dispatch(savePhotos())}
          data-track="form-save"
          data-track-form-name="Submit Claim Photos"
          data-track-form-step="step 8 - photos"
          element="button"
          text={isFetching ? 'Saving...' : saveText}
          type="submit"
          disabled={!validated}
        />
      )}
      {saveText === 'Next' && (
        <Subscript
          data-track="form-advance"
          data-track-form-name="Submit Claim Photos"
          data-track-form-step="step 8 - skip this step for now"
          onClick={goNext}
        >
          Skip this step for now
        </Subscript>
      )}

      <Modal open={modalOpen && modalType} onClose={() => setModal(false)}>
        <div className="modal-content">
          <h2>Examples Photos</h2>
          <PhotoWrap>
            {modalType === 'frontOfHouse' && (
              <>
                <p>Front of House</p>
                <div className="images">
                  <img src={PhotoFrontImg} alt="photos back" />
                  <img src={PhotoFrontImg2} alt="photos front" />
                </div>
              </>
            )}
            {modalType === 'backOfHouse' && (
              <>
                <p>Back of House Photos</p>
                <div className="images">
                  <img src={PhotoBack} alt="photos back" />
                  <img src={PhotoBack2} alt="photos back" />
                </div>
              </>
            )}
            {modalType === 'wideAngle' && (
              <>
                <p>Wide Angle Photos</p>
                <div className="images">
                  <img src={PhotoWideAngle} alt="photos wide angle" />
                  <img src={PhotoWideAngle2} alt="photos wide angle2" />
                  <img src={PhotoWideAngle3} alt="photos wide angle3" />
                  <img src={PhotoWideAngle4} alt="photos wide angle4" />
                </div>
              </>
            )}
            {modalType === 'closeUp' && (
              <>
                <p>Close Up Photos</p>
                <div className="images">
                  <img src={PhotoCloseUp} alt="close up photos" />
                  <img src={PhotoCloseUp2} alt="close up photos2" />
                  <img src={PhotoCloseUp3} alt="close up photos3" />
                  <img src={PhotoCloseUp4} alt="close up photos4" />
                </div>
              </>
            )}
            {modalType === 'intakeVent' && (
              <>
                <p>Intake Ventilation Photos</p>
                <div className="images">
                  <img src={PhotoVent2} alt="ventilations photos" />
                  <img src={PhotoVent4} alt="ventilations photos" />
                </div>
              </>
            )}
            {modalType === 'exhaustVent' && (
              <>
                <p>Exhaust Ventilation Photos</p>
                <div className="images">
                  <img src={PhotoVent} alt="ventilations photos" />
                  <img src={PhotoVent3} alt="ventilations photos" />
                </div>
              </>
            )}
            {modalType === 'shingleFront' && (
              <>
                <p>Front of Shingle</p>
                <div className="images">
                  <img src={PhotoShingle} alt="shingle front photos" />
                </div>
              </>
            )}
            {modalType === 'shingleBack' && (
              <>
                <p>Back of Shingle</p>
                <div className="images">
                  <img src={PhotoShingle2} alt="shingle back" />
                </div>
              </>
            )}
            {modalType === 'releaseTape' && (
              <>
                <p>Release Tape Photo</p>
                <div className="images">
                  <img src={ReleaseTapeImg} alt="release tap" />
                </div>
              </>
            )}
            {modalType === 'fasteners' && (
              <>
                <p>Fasteners Photos</p>
                <div className="images">
                  <img src={PhotoFastener} alt="fasteners photos" />
                  <img src={PhotoFastener2} alt="fasteners photos2" />
                  <img src={PhotoFastener3} alt="fasteners photos3" />
                  <img src={PhotoFastener4} alt="fasteners photos4" />
                </div>
              </>
            )}
          </PhotoWrap>
        </div>
      </Modal>
    </>
  );
};

export default WarrantyPhotos;
